import type { Dropdown, Tooltip } from 'bootstrap';

import { getBootstrapDropdown, getBootstrapTooltip } from './bootstrap-runtime';

type TooltipOptions = ConstructorParameters<typeof Tooltip>[1];

const TOOLTIP_EXCLUDE_SELECTOR = [
    'script',
    'style',
    'template',
    'link',
    'meta',
    'iframe',
    'object',
    'embed',
    'option',
    '[data-nais-no-tooltip]',
    '.tooltip',
    '.tooltip-inner',
].join(', ');

const OTHER_BS_TOGGLE = new Set([
    'popover',
    'modal',
    'offcanvas',
    'collapse',
    'tab',
    'dropdown',
    'pill',
    'list',
    'button',
]);

export const NAIS_TOOLTIP_OPTIONS: TooltipOptions = {
    customClass: 'nais-tooltip',
    container: 'body',
    boundary: 'viewport',
    delay: { show: 320, hide: 80 },
    trigger: 'hover focus',
};

function bs() {
    return {
        Tooltip: getBootstrapTooltip(),
        Dropdown: getBootstrapDropdown(),
    };
}

function getTooltipText(el: HTMLElement): string | null {
    const text = el.getAttribute('data-bs-title')
        ?? el.getAttribute('title')
        ?? el.getAttribute('data-bs-original-title');

    if (!text?.trim()) {
        return null;
    }

    return text.trim();
}

function isTooltipCandidate(el: HTMLElement): boolean {
    if (!getTooltipText(el)) {
        return false;
    }

    if (el.closest('#tracy-debug, .tracy-panel, [id^="tracy-"]')) {
        return false;
    }

    if (el.matches(TOOLTIP_EXCLUDE_SELECTOR)) {
        return false;
    }

    // Dropdown toggle shares the element — tooltip stays on focus and stacks on body after AJAX.
    if (el.getAttribute('data-bs-toggle') === 'dropdown') {
        return false;
    }

    return true;
}

function findTooltipCandidates(root: ParentNode): HTMLElement[] {
    const nodes = root.querySelectorAll<HTMLElement>(
        '[data-bs-toggle="tooltip"], [data-bs-toggle="bsTooltip"], [title], [data-bs-title]',
    );

    return Array.from(nodes).filter((el) => isTooltipCandidate(el));
}

function normalizeTooltipElement(el: HTMLElement): void {
    if (el.getAttribute('data-bs-toggle') === 'bsTooltip') {
        el.setAttribute('data-bs-toggle', 'tooltip');
    }
}

function getTooltipOptions(el: HTMLElement): TooltipOptions {
    const options: TooltipOptions = {
        ...NAIS_TOOLTIP_OPTIONS,
        title: getTooltipText(el) ?? undefined,
    };

    const html = el.getAttribute('data-bs-html') ?? el.getAttribute('data-html');
    if (html === 'true') {
        options.html = true;
    }

    const placement = el.getAttribute('data-bs-placement') ?? el.getAttribute('data-placement');
    if (placement) {
        options.placement = placement as TooltipOptions['placement'];
    }

    return options;
}

function initTooltipElement(el: HTMLElement): void {
    const api = bs();

    normalizeTooltipElement(el);

    const text = getTooltipText(el);
    if (!text) {
        return;
    }

    const toggle = el.getAttribute('data-bs-toggle');
    const options = getTooltipOptions(el);

    try {
        if (api.Tooltip.getInstance(el)) {
            return;
        }

        // Modal/dropdown/etc. keep their data-bs-toggle; tooltip is JS-only on hover.
        if (toggle && OTHER_BS_TOGGLE.has(toggle)) {
            api.Tooltip.getOrCreateInstance(el, options);
            return;
        }

        if (toggle !== 'tooltip') {
            el.setAttribute('data-bs-toggle', 'tooltip');
        }

        api.Tooltip.getOrCreateInstance(el, options);
    } catch {
        /* element not in DOM or invalid state after AJAX snippet */
    }
}

export function initBootstrapTooltips(root: ParentNode = document): void {
    findTooltipCandidates(root).forEach((el) => {
        initTooltipElement(el);
    });
}

/**
 * Bootstrap `BaseComponent.dispose()` nulls every field (including `_activeTrigger`).
 * `hide()` queues a transition callback that later calls `_isWithActiveTrigger()` —
 * so never `hide()` + `dispose()` back-to-back (Object.values(null) throws).
 */
function disposeTooltip(el: HTMLElement): void {
    const tip = getBootstrapTooltip().getInstance(el);
    if (!tip) {
        return;
    }
    try {
        tip.dispose();
    } catch {
        /* ignore */
    }
}

type TooltipInternal = Tooltip & {
    _element?: HTMLElement | null;
    _activeTrigger?: Record<string, boolean> | null;
    _isWithActiveTrigger?: () => boolean;
    _queueCallback?: (callback: () => void, element: Element | null, isAnimated?: boolean) => void;
};

let tooltipLifecyclePatchInstalled = false;

function isVisibleTooltipElement(el: HTMLElement): boolean {
    if (!el.isConnected || el.closest('[hidden], .d-none')) {
        return false;
    }

    const style = window.getComputedStyle(el);

    return style.visibility === 'visible'
        && style.display !== 'none'
        && el.getClientRects().length > 0;
}

/**
 * Opening a side dialog hides/disposes the trigger tooltip while show()/hide()
 * still have a transition callback queued. Bootstrap then touches `_element`
 * after `BaseComponent.dispose()` nulled it (`removeAttribute` / `style`).
 */
function installTooltipLifecyclePatch(): void {
    if (tooltipLifecyclePatchInstalled) {
        return;
    }
    tooltipLifecyclePatchInstalled = true;

    const proto = getBootstrapTooltip().prototype as TooltipInternal;

    const originalIsWithTrigger = proto._isWithActiveTrigger;
    if (typeof originalIsWithTrigger === 'function') {
        proto._isWithActiveTrigger = function naisIsWithActiveTrigger(this: TooltipInternal) {
            if (!this._element || !this._activeTrigger) {
                return false;
            }
            return Object.values(this._activeTrigger).includes(true);
        };
    }

    const originalQueue = proto._queueCallback;
    if (typeof originalQueue === 'function') {
        proto._queueCallback = function naisTooltipQueueCallback(
            this: TooltipInternal,
            callback: () => void,
            element: Element | null,
            isAnimated?: boolean,
        ) {
            const wrapped = (): void => {
                if (!this._element) {
                    return;
                }
                callback.call(this);
            };
            originalQueue.call(this, wrapped, element, isAnimated);
        };
    }

    const originalShow = proto.show;
    if (typeof originalShow === 'function') {
        proto.show = function naisTooltipShow(this: TooltipInternal) {
            if (!this._element || !isVisibleTooltipElement(this._element)) {
                return;
            }

            try {
                originalShow.call(this);
            } catch (error) {
                if (error instanceof Error && error.message.includes('visible elements')) {
                    return;
                }

                throw error;
            }
        };
    }

    const originalHide = proto.hide;
    if (typeof originalHide === 'function') {
        proto.hide = function naisTooltipHide(this: TooltipInternal) {
            if (!this._element) {
                return;
            }
            originalHide.call(this);
        };
    }
}

export function hideBootstrapTooltips(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>(
        '[data-bs-toggle="tooltip"], [data-bs-toggle="dropdown"], [title], [data-bs-title]',
    ).forEach((el) => {
        const tip = getBootstrapTooltip().getInstance(el) as TooltipInternal | null;
        if (!tip) {
            return;
        }
        try {
            // Only hide — dispose would null `_activeTrigger` while hide()'s
            // transition callback is still queued (see disposeTooltip).
            if (tip._activeTrigger) {
                tip.hide();
            }
        } catch {
            /* ignore */
        }
    });

    document.querySelectorAll<HTMLElement>('body > .tooltip, .tooltip.nais-tooltip').forEach((tipEl) => {
        tipEl.remove();
    });
}

function clearOrphanTooltipNodes(): void {
    document.querySelectorAll<HTMLElement>('.tooltip.nais-tooltip').forEach((tipEl) => {
        tipEl.remove();
    });
}

let dropdownTooltipGuardInstalled = false;

function installDropdownTooltipGuard(): void {
    if (dropdownTooltipGuardInstalled) {
        return;
    }
    dropdownTooltipGuardInstalled = true;

    document.addEventListener('show.bs.dropdown', (event) => {
        const toggle = event.target;
        if (!(toggle instanceof HTMLElement)) {
            return;
        }
        disposeTooltip(toggle);
        hideBootstrapTooltips();
        clearOrphanTooltipNodes();
    });
}

function installJqueryTooltipShim(): void {
    const $ = window.jQuery;
    if (!$?.fn || ($.fn.tooltip as { _naisBs5?: boolean })?._naisBs5) {
        return;
    }

    $.fn.tooltip = function tooltipShim(action?: string) {
        if (action === 'hide') {
            this.each(function hideEach() {
                const tip = getBootstrapTooltip().getInstance(this) as TooltipInternal | null;
                if (tip?._activeTrigger) {
                    tip.hide();
                }
            });
        }

        if (action === 'dispose' || action === 'destroy') {
            this.each(function disposeEach() {
                disposeTooltip(this);
            });
        }

        return this;
    };
    ($.fn.tooltip as { _naisBs5?: boolean })._naisBs5 = true;
}

function normalizeLegacyBootstrapAttributes(root: ParentNode = document): void {
    const legacyToggleMap: Record<string, string> = {
        dropdown: 'dropdown',
        collapse: 'collapse',
        tab: 'tab',
        pill: 'pill',
        modal: 'modal',
        tooltip: 'tooltip',
        popover: 'popover',
        button: 'button',
    };

    root.querySelectorAll<HTMLElement>('[data-toggle]').forEach((el) => {
        const legacy = el.getAttribute('data-toggle');
        if (!legacy || el.getAttribute('data-bs-toggle')) {
            return;
        }

        const mapped = legacyToggleMap[legacy];
        if (!mapped) {
            return;
        }

        el.setAttribute('data-bs-toggle', mapped);
        el.removeAttribute('data-toggle');
    });

    root.querySelectorAll<HTMLElement>('[data-target]').forEach((el) => {
        if (el.getAttribute('data-bs-target')) {
            return;
        }

        const target = el.getAttribute('data-target');
        if (!target) {
            return;
        }

        el.setAttribute('data-bs-target', target);
        el.removeAttribute('data-target');
    });

    root.querySelectorAll<HTMLElement>('[data-dismiss]').forEach((el) => {
        if (el.getAttribute('data-bs-dismiss')) {
            return;
        }

        const dismiss = el.getAttribute('data-dismiss');
        if (!dismiss) {
            return;
        }

        el.setAttribute('data-bs-dismiss', dismiss);
        el.removeAttribute('data-dismiss');
    });

    root.querySelectorAll<HTMLElement>('button.close[data-bs-dismiss], button.close[data-dismiss]').forEach((el) => {
        el.classList.remove('close');
        el.classList.add('btn-close');
        el.innerHTML = '';
    });
}

export function initBootstrapComponents(root: ParentNode = document): void {
    normalizeLegacyBootstrapAttributes(root);
    installTooltipLifecyclePatch();
    installJqueryTooltipShim();
    installDropdownOverflowPortal();
    installDropdownItemAutoClose();
    installDropdownTooltipGuard();
    initBootstrapTooltips(root);

    root.querySelectorAll<HTMLElement>('[data-bs-toggle="dropdown"]').forEach((el) => {
        try {
            disposeTooltip(el);
            // Drop native title so the browser tip does not stack with BS leftovers
            if (el.hasAttribute('title')) {
                if (!el.getAttribute('aria-label')) {
                    el.setAttribute('aria-label', el.getAttribute('title') ?? '');
                }
                el.removeAttribute('title');
            }
            el.removeAttribute('data-bs-title');

            if (getBootstrapDropdown().getInstance(el)) {
                return;
            }
            const clipped = Boolean(
                el.closest('.nais-datagrid-panel, .nais-datagrid-panel__table, .nais-sd-body, .nais-sd-window'),
            );
            getBootstrapDropdown().getOrCreateInstance(
                el,
                clipped
                    ? {
                          popperConfig: {
                              strategy: 'fixed',
                          },
                      }
                    : undefined,
            );
        } catch {
            /* ignore */
        }
    });
}

type PortaledMenu = HTMLElement & { _naisDropdownHome?: ParentNode | null };

function getDropdownMenuEl(toggle: HTMLElement): HTMLElement | null {
    const instance = getBootstrapDropdown().getInstance(toggle) as { _menu?: HTMLElement } | null;
    if (instance?._menu) {
        return instance._menu;
    }

    if (toggle.nextElementSibling instanceof HTMLElement && toggle.nextElementSibling.matches('.dropdown-menu')) {
        return toggle.nextElementSibling;
    }

    const parentMenu = toggle.parentElement?.querySelector(':scope > .dropdown-menu');
    return parentMenu instanceof HTMLElement ? parentMenu : null;
}

let dropdownPortalInstalled = false;

/**
 * Menus inside overflow:auto/clip ancestors (datagrid panel/table) get clipped.
 * Portal to <body> while open so Popper `strategy: fixed` can actually show them.
 */
function installDropdownOverflowPortal(): void {
    if (dropdownPortalInstalled) {
        return;
    }
    dropdownPortalInstalled = true;

    document.addEventListener('show.bs.dropdown', (event) => {
        const toggle = event.target;
        if (!(toggle instanceof HTMLElement)) {
            return;
        }
        if (!toggle.closest('.nais-datagrid-panel, .nais-datagrid-panel__table, td.row-actions, .nais-sd-body')) {
            return;
        }

        const menu = getDropdownMenuEl(toggle) as PortaledMenu | null;
        if (!menu || menu.parentElement === document.body) {
            return;
        }

        menu._naisDropdownHome = menu.parentNode;
        document.body.appendChild(menu);
    });

    document.addEventListener('hidden.bs.dropdown', (event) => {
        const toggle = event.target;
        if (!(toggle instanceof HTMLElement)) {
            return;
        }

        const menu = getDropdownMenuEl(toggle) as PortaledMenu | null;
        if (!menu?._naisDropdownHome) {
            return;
        }

        menu._naisDropdownHome.appendChild(menu);
        menu._naisDropdownHome = null;
    });
}

let dropdownItemAutoCloseInstalled = false;

/**
 * Nette AJAX on `.dropdown-item` (sort / export / actions) often preventsDefault /
 * stopPropagation, so Bootstrap never auto-closes — especially after the menu is
 * portaled to <body>. Close the owning dropdown on item click (capture phase).
 */
function installDropdownItemAutoClose(): void {
    if (dropdownItemAutoCloseInstalled) {
        return;
    }
    dropdownItemAutoCloseInstalled = true;

    document.addEventListener(
        'click',
        (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }

            const item = target.closest('.dropdown-menu .dropdown-item');
            if (!(item instanceof HTMLElement)) {
                return;
            }

            const menu = item.closest('.dropdown-menu');
            if (!(menu instanceof HTMLElement)) {
                return;
            }

            const labelledBy = menu.getAttribute('aria-labelledby');
            const toggle = labelledBy
                ? document.getElementById(labelledBy)
                : document.querySelector<HTMLElement>(
                      `[data-bs-toggle="dropdown"][aria-expanded="true"]`,
                  );

            if (!(toggle instanceof HTMLElement)) {
                return;
            }

            try {
                getBootstrapDropdown().getInstance(toggle)?.hide();
            } catch {
                /* ignore */
            }
        },
        true,
    );
}

(window as Window & {
    initBootstrapTooltips?: typeof initBootstrapTooltips;
    hideBootstrapTooltips?: typeof hideBootstrapTooltips;
}).initBootstrapTooltips = initBootstrapTooltips;

(window as Window & { hideBootstrapTooltips?: typeof hideBootstrapTooltips }).hideBootstrapTooltips =
    hideBootstrapTooltips;
